import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, Receipt } from "@mui/icons-material";
import {
  createBill as createBillApi,
  getChildren as getChildrenApi,
  getCurrencies,
} from "apis";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Child } from "types";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import Flag from "react-world-flags";

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  flag: string;
  rate: string;
  baseCurrency: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onBillAdd: () => void;
}

interface FormFields {
  heading: string;
  ref: string;
  type: string;
  currencyId: string;
  amount: string;
  childId: string;
  status: number;
  billingStartDate: string;
  billingEndDate: string;
}

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const AddBillDialog: React.FC<Props> = ({ open, onClose, onBillAdd }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [children, setChildren] = useState<Child[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      heading: "",
      ref: "",
      type: "Bill",
      currencyId: "",
      amount: "",
      childId: "",
      status: 0,
      billingStartDate: "",
      billingEndDate: "",
    },
  });

  useEffect(() => {
    getChildrenApi().then((data) => setChildren(data));
  }, []);

  useEffect(() => {
    getCurrencies().then((data) => setCurrencies(data));
  }, []);

  const onSubmit: SubmitHandler<FormFields> = async (values) => {
    setSubmitting(true);
    try {
      await createBillApi(values);
      enqueueSnackbar(t("bill.added"), { variant: "success" });
      onBillAdd();
      onClose();
    } catch (err: any) {
      if (err?.response?.status === 400) {
        enqueueSnackbar(err.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(t("bill.create_error"), { variant: "error" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Receipt sx={{ fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700}>
            {t("bill.add")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("labels.heading")}
                required
                fullWidth
                size="small"
                placeholder="e.g. May 2026 — Adam"
                {...register("heading", { required: true })}
                error={!!errors.heading}
                helperText={
                  errors.heading ? t("validation.required") : undefined
                }
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("labels.ref")}
                required
                fullWidth
                size="small"
                placeholder="e.g. INV-2026-05-001"
                {...register("ref", { required: true })}
                error={!!errors.ref}
                helperText={errors.ref ? t("validation.required") : undefined}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!errors.currencyId}>
                <InputLabel>{t("labels.currency")}</InputLabel>
                <Controller
                  name="currencyId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t("labels.currency")}
                      sx={inputSx}
                      renderValue={(selected) => {
                        const curr = currencies.find(
                          (c) => c.id === Number(selected),
                        );
                        if (!curr) return null;
                        return (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 22,
                                height: 14,
                                overflow: "hidden",
                                borderRadius: "2px",
                                flexShrink: 0,
                              }}
                            >
                              <Flag code={curr.flag} />
                            </Box>
                            <span>{curr.code}</span>
                            <span style={{ color: "#888" }}>{curr.symbol}</span>
                          </Box>
                        );
                      }}
                    >
                      {currencies.map((currency) => (
                        <MenuItem
                          key={currency.id}
                          value={currency.id}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 22,
                              height: 14,
                              overflow: "hidden",
                              borderRadius: "2px",
                              flexShrink: 0,
                            }}
                          >
                            <Flag code={currency.flag} />
                          </Box>
                          <Box flexGrow={1}>{currency.name}</Box>
                          <Box color="text.secondary">
                            {currency.code} — {currency.symbol}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.currencyId && (
                  <FormHelperText>{t("labels.currency")}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("labels.amount")}
                fullWidth
                size="small"
                type="number"
                {...register("amount", { required: true })}
                error={!!errors.amount}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t("labels.startDate")}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register("billingStartDate")}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t("labels.endDate")}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register("billingEndDate")}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!errors.childId}>
                <InputLabel>{t("labels.child")}</InputLabel>
                <Controller
                  name="childId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select {...field} label={t("labels.child")} sx={inputSx}>
                      <MenuItem value="">
                        <em>{t("placeholders.select_child")}</em>
                      </MenuItem>
                      {children.map((child) => (
                        <MenuItem key={child.id} value={child.id}>
                          {`${child.firstName} ${child.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.childId && (
                  <FormHelperText>{t("labels.child")}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!errors.type}>
                <InputLabel>{t("inputs.type")}</InputLabel>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select {...field} label={t("inputs.type")} sx={inputSx}>
                      <MenuItem value="Bill">{t("labels.bill")}</MenuItem>
                      <MenuItem value="Receipt">
                        {t("labels.invoices")}
                      </MenuItem>
                    </Select>
                  )}
                />
                {errors.type && (
                  <FormHelperText>{t("inputs.type")}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.add")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default AddBillDialog;
